// Every address the admin module owns or sends people to. The rebuilt admin
// lives under `/admin`, outside the patient dashboard (XCUT-R17), and is where
// admins land (XCUT-R29); the legacy admin keeps `/dashboard/admin` inside it
// (XCUT-R15).
export const adminRoutePaths = {
  home: "/admin",
  // The User, Case and Order searches stay at the admin's own address (SHELL-R10).
  find: "/admin",
  insightsHome: "/admin/home",
  pharmacyRouting: "/admin/pharmacy-routing",
  redo: "/admin/redo",
  ship: "/admin/ship",
  uiKit: "/admin/ui-kit",
  legacyAdmin: "/dashboard/admin",
  patientDashboard: "/dashboard",
  nonAdminLanding: "/dashboard/cases",
};

// The patient dashboard sends every admin to the rebuilt admin on mount (XCUT-R29).
// An admin who chose "Patient dashboard" from the rebuilt admin carries this
// marker in the router location state so that visit is left alone (XCUT-R20);
// the browser keeps the state through a reload of that page.
interface PatientDashboardVisitFromAdmin {
  patientDashboardVisitFromAdmin: true;
}

export const patientDashboardVisitFromAdmin: { pathname: string; state: PatientDashboardVisitFromAdmin } = {
  pathname: adminRoutePaths.patientDashboard,
  state: { patientDashboardVisitFromAdmin: true },
};

export const isPatientDashboardVisitFromAdmin = (locationState: unknown): boolean =>
  typeof locationState === "object" &&
  locationState !== null &&
  (locationState as Partial<PatientDashboardVisitFromAdmin>).patientDashboardVisitFromAdmin ===
    true;

const isLegacyAdminAddress = (pathname: string): boolean =>
  pathname.replace(/\/+$/, "") === adminRoutePaths.legacyAdmin;

// Where the patient dashboard sends an admin who lands on it: the rebuilt admin
// (XCUT-R29), except on the legacy admin (XCUT-R30) or a visit chosen from the
// rebuilt admin (XCUT-R20), which stay where they are.
export const rebuiltAdminRedirectFor = (location: {
  pathname: string;
  state: unknown;
}): string | null => {
  if (isLegacyAdminAddress(location.pathname)) return null;
  if (isPatientDashboardVisitFromAdmin(location.state)) return null;

  return adminRoutePaths.home;
};

// The two searches the top bar can open (XCUT-R6): an entry with an "@" is an email
// for User Search, anything else is an Order ID or Case ID for Order Search.
export const globalSearchKinds = { user: "user", order: "order" } as const;

export const globalSearchAddress = (entry: string): string | null => {
  const term = entry.trim();
  if (term === "") return null;

  const kind = term.includes("@") ? globalSearchKinds.user : globalSearchKinds.order;

  return `${adminRoutePaths.find}?search=${kind}&q=${encodeURIComponent(term)}`;
};
